import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Company } from '@app/models/user.model';
import { ClosedownMode } from '@app/models/closedown-detail.model';
import { AuthService } from '@app/services/auth/auth.service';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-workbench-header',
  templateUrl: './workbench-header.component.html',
  styleUrls: ['./workbench-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WorkbenchHeaderComponent implements OnInit {
  readonly jobsMenu = input<MatMenu>();
  readonly invoicesMenu = input<MatMenu>();
  readonly mode = input<ClosedownMode>('admin');
  readonly resourceCompanyId = input<string | null>(null);
  readonly isRedirectingToWorkbench = signal(false);
  readonly isMobileNavOpen = signal(false);
  readonly companies = signal<Company[]>([]);
  readonly visibleCompanies = computed(() => this.mode() === 'technician'
    ? this.companies().filter(company => company.id === this.resourceCompanyId())
    : this.companies());
  readonly currentProfileId = signal<string | null>(null);
  readonly isSwitchingProfile = signal(false);
  readonly selectedCompany = computed<Company | null>(() =>
    this.mode() === 'technician' ? this.visibleCompanies()[0] ?? null
    : this.visibleCompanies().find(company => company.id === this.currentProfileId()) ?? null);
  readonly companyLoadError = signal(false);

  private readonly authService = inject(AuthService);
  private readonly closedownService = inject(ClosedownService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  ngOnInit(): void {
    this.authService.getProfileCompanies().then(
      async companies => {
        this.companies.set(companies);
        let savedProfile = await this.authService.getCurrentProfile();
        if (savedProfile && !companies.some(company => company.id === savedProfile)) {
          await this.authService.clearCurrentProfile();
          savedProfile = null;
        }
        this.currentProfileId.set(savedProfile);
      }
    ).catch(
      () => {
        this.companyLoadError.set(true);
      }
    );
  }

  toggleMobileNav(): void {
    this.isMobileNavOpen.update(isOpen => !isOpen);
  }

  async selectCompany(company: Company): Promise<void> {
    if (this.mode() === 'technician' || this.isSwitchingProfile()) {
      return;
    }
    this.isSwitchingProfile.set(true);
    try {
      if (await this.authService.setCurrentProfile(company.id)) {
        this.currentProfileId.set(company.id);
        this.authService.reloadPage();
      } else {
        this.companyLoadError.set(true);
      }
    } finally {
      this.isSwitchingProfile.set(false);
    }
  }

  logout(): void {
    if (confirm(this.translateService.instant('appcomponent_dialog_logout'))) {
      void this.router.navigate(['/logout']);
    }
  }

  async selectAllProfiles(): Promise<void> {
    if (this.mode() === 'technician' || this.isSwitchingProfile()) {
      return;
    }
    this.isSwitchingProfile.set(true);
    try {
      await this.authService.clearCurrentProfile();
      this.currentProfileId.set(null);
      this.authService.reloadPage();
    } catch {
      this.companyLoadError.set(true);
    } finally {
      this.isSwitchingProfile.set(false);
    }
  }

  async openWorkbench(): Promise<void> {
    if (this.isRedirectingToWorkbench()) {
      return;
    }

    this.isRedirectingToWorkbench.set(true);
    try {
      await this.closedownService.directToClosedown(false);
    } finally {
      this.isRedirectingToWorkbench.set(false);
    }
  }
}
